/**
 * Access requests — wire schemas.
 *
 *   GET  /api/v1/tenants/{tenant_id}/access-requests
 *   POST /api/v1/tenants/{tenant_id}/access-requests/{id}/approve
 *   POST /api/v1/tenants/{tenant_id}/access-requests/{id}/dismiss
 *
 * The queue behind the console sign-in screen's "Request access"
 * affordance: an administrator who cannot sign in — never enrolled,
 * or every passkey removed — asking to be let back in.
 */

import { z } from "zod";

// Which door the requester was standing at. Both resolve to the same
// operator action, but the judgement differs: re_enrollment is also
// what a successful account takeover looks like from this side.
export const accessRequestKindSchema = z.enum(["activation", "re_enrollment"]);
export type AccessRequestKind = z.infer<typeof accessRequestKindSchema>;

export const accessRequestSchema = z.object({
  id: z.string().uuid(),
  kind: accessRequestKindSchema,
  status: z.enum(["pending", "approved", "dismissed"]),

  admin_user_id: z.string().uuid(),
  admin_email: z.string(),
  admin_name: z.string(),
  admin_status: z.string(),

  first_requested_at: z.string(),
  last_requested_at: z.string(),
  request_count: z.number().int().min(1),

  request_ip: z.string(),
  request_user_agent: z.string(),
});
export type AccessRequest = z.infer<typeof accessRequestSchema>;

export const accessRequestListSchema = z.object({
  items: z.array(accessRequestSchema),
  pending: z.number().int().nonnegative(),
});
export type AccessRequestList = z.infer<typeof accessRequestListSchema>;

export const approveResponseSchema = z.object({
  request: accessRequestSchema,
  // Whether the enrollment invitation actually went out. Approving
  // without it is a state the operator must not mistake for done, so
  // it is on the wire rather than assumed.
  invitation_sent: z.boolean(),
  warnings: z.array(z.string()).optional(),
});
export type ApproveResponse = z.infer<typeof approveResponseSchema>;

export const KIND_LABEL: Record<AccessRequestKind, string> = {
  activation: "Never enrolled",
  re_enrollment: "Passkeys removed",
};

export const KIND_HELP: Record<AccessRequestKind, string> = {
  activation:
    "This administrator has never held a passkey — their invitation expired, or was never opened. Approving sends a fresh enrollment link.",
  re_enrollment:
    "Every passkey on this account was removed. Approving sends a fresh enrollment link — but check first that the request is really from them, because this is also what a successful account takeover looks like.",
};
